/**
 * Web component for auto-scrolling scrollable containers to the bottom
 *
 * Usage:
 *   <anear-autoscroll behavior="smooth">
 *     <div class="messages-container">
 *       <!-- messages -->
 *     </div>
 *   </anear-autoscroll>
 *
 * Or as an attribute on the scrollable element itself:
 *   <div class="messages-container" data-anear-autoscroll="smooth">
 *     <!-- messages -->
 *   </div>
 *
 * The component automatically scrolls to the bottom when:
 * - The component is connected to the DOM
 * - New child elements are added to the scrollable container
 * - The container's content changes
 */
class AnearAutoScroll extends HTMLElement {
  constructor() {
    super()
    this._behavior = null
    this._observer = null
    this._scrollContainer = null
  }

  static get observedAttributes() {
    return ['behavior']
  }

  connectedCallback() {
    // Read the behavior attribute if it exists
    const behaviorAttr = this.getAttribute('behavior')
    if (behaviorAttr !== null && this._behavior === null) {
      this._behavior = behaviorAttr
    }

    // Find the scrollable container - either this element itself or its first child
    // Check if this element is scrollable
    if (this.scrollHeight > this.clientHeight ||
        getComputedStyle(this).overflowY === 'auto' ||
        getComputedStyle(this).overflowY === 'scroll') {
      this._scrollContainer = this
    } else {
      // Try to find a scrollable child element
      const children = Array.from(this.children)
      this._scrollContainer = children.find(el => {
        const style = getComputedStyle(el)
        return (el.scrollHeight > el.clientHeight) ||
               style.overflowY === 'auto' ||
               style.overflowY === 'scroll'
      }) || this.firstElementChild || this
    }

    // Initial scroll to bottom
    this.scrollToBottom()

    // Set up MutationObserver to watch for new content
    this.setupObserver()
  }

  disconnectedCallback() {
    // Clean up observer
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
    this._scrollContainer = null
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'behavior') {
      this._behavior = newValue
      // Re-scroll when behavior changes
      if (this._scrollContainer) {
        this.scrollToBottom()
      }
    }
  }

  get behavior() {
    return this.getAttribute('behavior') || this._behavior || 'auto'
  }

  set behavior(value) {
    if (value) {
      this.setAttribute('behavior', value)
      this._behavior = value
    } else {
      this.removeAttribute('behavior')
      this._behavior = null
    }
    if (this._scrollContainer) {
      this.scrollToBottom()
    }
  }

  /**
   * Scroll the container to the bottom
   */
  scrollToBottom() {
    if (!this._scrollContainer) return

    try {
      const behavior = this.behavior === 'smooth' ? 'smooth' : 'auto'
      this._scrollContainer.scrollTo({
        top: this._scrollContainer.scrollHeight,
        behavior: behavior
      })
    } catch (e) {
      // Fallback for older browsers
      try {
        this._scrollContainer.scrollTop = this._scrollContainer.scrollHeight
      } catch (_e) {
        // Ignore scroll errors
      }
    }
  }

  /**
   * Set up MutationObserver to watch for content changes
   */
  setupObserver() {
    if (!this._scrollContainer) return

    // Clean up existing observer
    if (this._observer) {
      this._observer.disconnect()
    }

    // Create observer to watch for new child elements
    this._observer = new MutationObserver((mutations) => {
      let shouldScroll = false
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          shouldScroll = true
        }
      })

      if (shouldScroll) {
        // Use requestAnimationFrame to ensure DOM has fully updated
        requestAnimationFrame(() => {
          this.scrollToBottom()
        })
      }
    })

    // Observe changes to the scroll container
    this._observer.observe(this._scrollContainer, {
      childList: true,
      subtree: true
    })
  }
}

// Register the custom element
if (!customElements.get('anear-autoscroll')) {
  customElements.define('anear-autoscroll', AnearAutoScroll)
}

export default AnearAutoScroll

